import { useState, useEffect } from 'react';
import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';
import { TextField } from 'formik-mui';
import FormHelperText from '@mui/material/FormHelperText';
import { useRouter } from 'next/router';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Button, Dropdown, FadeInAnimation } from '../shared';
import { contactUsDomain } from '../../domain';

import useResponsive from '../../../utils/responsive';

const partnersInitialValues = {
    enquiry: '',
    title: '',
    nameAndSurname: '',
    emailAddress: '',
    contactNumber: '',
    company: '',
    numberOfBuildings: '',
    city: '',
    country: '',
    typeOfBusiness: '',
    sizeOfRental: '',
};

const partnersValidationSchema = Yup.object({
    title: Yup.string('').required('Title is required'),
    numberOfBuildings: Yup.string('').required('Number of buildings is required'),
    nameAndSurname: Yup.string('').required('Name and surname is required'),
    emailAddress: Yup.string('').email('Enter a valid email').required('Email is required'),
    contactNumber: Yup.string('')
        .matches(/^[0-9,+]*$/, 'Phone number is invalid')
        .min(9, 'Phone number is too short (minimum 9)')
        .max(15, 'Phone number is too long (maximum 15)')
        .required('Phone number is required'),
});

const otherValidationSchema = Yup.object({
    title: Yup.string('').required('Title is required'),
    nameAndSurname: Yup.string('').required('Name and surname is required'),
    emailAddress: Yup.string('').email('Enter a valid email').required('Email is required'),
    contactNumber: Yup.string('')
        .matches(/^[0-9,+]*$/, 'Phone number is invalid')
        .min(9, 'Phone number is too short (minimum 9)')
        .max(15, 'Phone number is too long (maximum 15)')
        .required('Phone number is required'),
});

const enquiryTypes = [
    {
        label: 'Partners & Landlords',
        value: 'Partners & Landlords',
        id: 'partners-and-landlords-select',
    },
    {
        label: 'Investing with HOMii',
        value: 'Investing with HOMii',
        id: 'investing-select',
    },
    {
        label: 'Commercial Rental',
        value: 'Commercial Rental',
        id: 'commercial-rental-select',
    },
];

const titles = [
    {
        label: 'Mr',
        value: 'Mr',
        id: 'mr-select',
    },
    {
        label: 'Mrs',
        value: 'Mrs',
        id: 'mrs-select',
    },
    {
        label: 'Ms',
        value: 'Ms',
        id: 'ms-select',
    },
];

const buildings = [
    {
        label: '1-3 Buildings',
        value: '1-3 Buildings',
        id: '1-3-buildings-select',
    },
    {
        label: '4-6 Buildings',
        value: '4-6 Buildings',
        id: '4-6-buildings-select',
    },
    {
        label: '7+ Buildings',
        value: '7+ Buildings',
        id: 'more-than-7-buildings-select',
    },
];

const rentalSize = [
    {
        label: '0-150 SQM',
        value: '0-150 SQM',
        id: '0-150-SQM-select',
    },
    {
        label: '151-350 SQM',
        value: '151-351 SQM',
        id: '151-351-SQM-select',
    },
    {
        label: 'Above 350 SQM',
        value: 'Above 350 SQM',
        id: 'above-350-SQM-select',
    },
];

const PartnersForm = () => {
    const [titleType, setTitleType] = useState(null);
    const [buildingType, setBuildingType] = useState(null);
    const [rentalSizeType, setRentalSizeType] = useState(null);
    const [enquiryType, setEnquiryType] = useState('Partners & Landlords');
    const [contactUsSubmitError, setContactUsSubmitError] = useState(null);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
    const { isMobile, isTablet } = useResponsive();
    const router = useRouter();
    const pathRef = _.get(router, 'asPath').split('#')[1];

    useEffect(() => {
        if (pathRef === 'commercial-rental') {
            setEnquiryType('Commercial Rental');
        } else if (pathRef === 'investors') {
            setEnquiryType('Investing with HOMii');
        } else if (pathRef === 'main') {
            setEnquiryType('Partners & Landlords');
        }
    }, [pathRef]);

    const onSubmit = async ({
        title,
        nameAndSurname,
        emailAddress,
        contactNumber,
        company,
        numberOfBuildings,
        city,
        country,
        typeOfBusiness,
        sizeOfRental,
    }) => {
        try {
            if (enquiryType === 'Partners & Landlords') {
                await contactUsDomain.submitPartnersAndLandlords(
                    title,
                    nameAndSurname,
                    emailAddress,
                    contactNumber,
                    company,
                    numberOfBuildings,
                    city,
                    country,
                );
            } else if (enquiryType === 'Investing with HOMii') {
                await contactUsDomain.submitInvesting(title, nameAndSurname, emailAddress, contactNumber, company);
            } else if (enquiryType === 'Commercial Rental') {
                await contactUsDomain.submitCommercialRental(
                    title,
                    nameAndSurname,
                    emailAddress,
                    contactNumber,
                    company,
                    typeOfBusiness,
                    sizeOfRental,
                );
            }

            setContactUsSubmitError(null);
            setSuccessfullySubmitted(true);
        } catch (error) {
            setContactUsSubmitError(error.message);
            setSuccessfullySubmitted(false);
        }
    };

    const onTitleChanged = (_title, event, formikOnChange) => {
        setTitleType(_title);
        formikOnChange(event);
    };

    const onBuildingsChanged = (_building, event, formikOnChange) => {
        setBuildingType(_building);
        formikOnChange(event);
    };

    const onRentalSizeChange = (_rentalSize, event, formikOnChange) => {
        setRentalSizeType(_rentalSize);
        formikOnChange(event);
    };

    const onEnquiryChanged = (_enquiry, event, formikOnChange) => {
        setEnquiryType(_enquiry);
        formikOnChange(event);
    };

    const ThankYouMessage = () => {
        if (!successfullySubmitted) {
            return null;
        }

        return (
            <div className='get-in-touch-form-thank-you-container get-in-touch-form-thank-you-height-50'>
                <p className='dummy-header-three'>Thanks for Getting in Touch!</p>
                <p>Someone from HOMii will be in touch ASAP</p>
            </div>
        );
    };

    const DetailsOther = ({ touched, errors, handleChange, handleBlur }) => {
        if (isMobile || isTablet) {
            switch (enquiryType) {
                case 'Partners & Landlords':
                    return (
                        <>
                            <div className='get-in-touch-form-dropdown-container'>

                                <Dropdown
                                    placeholder="Number of Buildings*"
                                    selectId="buildings_dropdown"
                                    options={buildings}
                                    onChange={(_buildings, event) => {
                                        onBuildingsChanged(_buildings, event, handleChange);
                                    }}
                                    name="numberOfBuildings"
                                    defaultValue={buildingType}
                                    helperText={
                                        touched.numberOfBuildings ? errors.numberOfBuildings : ''
                                    }
                                    error={touched.numberOfBuildings && !!errors.numberOfBuildings}
                                    handleBlur={handleBlur}
                                />

                            </div>

                            <FormikField className='get-in-touch-form-field'
                                component={TextField}
                                name="company"
                                id="company"
                                type="text"
                                label="Company"
                                variant="outlined"
                                color="secondary"
                                placeholder="Company"
                                helperText={touched.company ? errors.company : ''}
                                error={touched.company && !!errors.company}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className='get-in-touch-form-field'
                                component={TextField}
                                name="city"
                                id="city"
                                type="text"
                                label="City"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.city ? errors.city : ''}
                                error={touched.city && !!errors.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className='get-in-touch-form-field'
                                component={TextField}
                                name="country"
                                id="country"
                                type="text"
                                label="Country"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.country ? errors.country : ''}
                                error={touched.country && !!errors.country}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />
                            
                        </>
                    );
                case 'Investing with HOMii':
                    return (
                        <FormikField className='get-in-touch-form-field'
                            component={TextField}
                            name="company"
                            id="company"
                            type="text"
                            label="Company"
                            variant="outlined"
                            color="secondary"
                            placeholder="Company"
                            helperText={touched.company ? errors.company : ''}
                            error={touched.company && !!errors.company}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                        />
                    );
                case 'Commercial Rental':
                    return (
                        <>
                            <div className='get-in-touch-form-dropdown-container'>

                                <Dropdown
                                    placeholder="Size of Rental"
                                    selectId="rental_size_dropdown"
                                    options={rentalSize}
                                    onChange={(_rentalSize, event) => {
                                        onRentalSizeChange(_rentalSize, event, handleChange);
                                    }}
                                    name="sizeOfRental"
                                    defaultValue={rentalSizeType}
                                    helperText={touched.sizeOfRental ? errors.sizeOfRental : ''}
                                    error={touched.sizeOfRental && !!errors.sizeOfRental}
                                    handleBlur={handleBlur}
                                />

                            </div>

                            <FormikField className='get-in-touch-form-field'
                                component={TextField}
                                name="company"
                                id="company"
                                type="text"
                                label="Company"
                                variant="outlined"
                                color="secondary"
                                placeholder="Company"
                                helperText={touched.company ? errors.company : ''}
                                error={touched.company && !!errors.company}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className='get-in-touch-form-field'
                                component={TextField}
                                name="typeOfBusiness"
                                id="typeOfBusiness"
                                type="text"
                                label="Type of Business"
                                variant="outlined"
                                color="secondary"
                                placeholder="Type of Business"
                                helperText={touched.typeOfBusiness ? errors.typeOfBusiness : ''}
                                error={touched.typeOfBusiness && !!errors.typeOfBusiness}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />
                        </>
                    );
                default:
                    return null;
            }
        }
        switch (enquiryType) {
            case 'Partners & Landlords':
                return (
                    <>
                        <div className='get-in-touch-form-input-container'>

                            <div className='get-in-touch-form-dropdown-container-right'>

                                <Dropdown
                                    placeholder="Number of Buildings*"
                                    selectId="buildings_dropdown"
                                    options={buildings}
                                    onChange={(_buildings, event) => {
                                        onBuildingsChanged(_buildings, event, handleChange);
                                    }}
                                    name="numberOfBuildings"
                                    defaultValue={buildingType}
                                    helperText={
                                        touched.numberOfBuildings ? errors.numberOfBuildings : ''
                                    }
                                    error={touched.numberOfBuildings && !!errors.numberOfBuildings}
                                    handleBlur={handleBlur}
                                />

                            </div>

                            <FormikField className='get-in-touch-form-field-right'
                                component={TextField}
                                name="company"
                                id="company"
                                type="text"
                                label="Company"
                                variant="outlined"
                                color="secondary"
                                placeholder="Company"
                                helperText={touched.company ? errors.company : ''}
                                error={touched.company && !!errors.company}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />
                        </div>

                        <div className='get-in-touch-form-input-container'>

                            <FormikField className='get-in-touch-form-field-left'
                                component={TextField}
                                name="city"
                                id="city"
                                type="text"
                                label="City"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.city ? errors.city : ''}
                                error={touched.city && !!errors.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                            <FormikField className='get-in-touch-form-field-right'
                                component={TextField}
                                name="country"
                                id="country"
                                type="text"
                                label="Country"
                                variant="outlined"
                                color="secondary"
                                helperText={touched.country ? errors.country : ''}
                                error={touched.country && !!errors.country}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                        </div>
                    </>
                );
            case 'Investing with HOMii':
                return (
                    <>
                       <div className='get-in-touch-form-input-container'>

                            <FormikField
                                component={TextField}
                                name="company"
                                id="company"
                                type="text"
                                label="Company"
                                variant="outlined"
                                color="secondary"
                                placeholder="Company"
                                helperText={touched.company ? errors.company : ''}
                                error={touched.company && !!errors.company}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />

                        </div>
                    </>
                );
            case 'Commercial Rental':
                return (
                    <>
                        <div className='get-in-touch-form-input-container'>

                            <div className='get-in-touch-form-dropdown-container-right'>

                                <Dropdown
                                    placeholder="Size of Rental"
                                    selectId="rental_size_dropdown"
                                    options={rentalSize}
                                    onChange={(_rentalSize, event) => {
                                        onRentalSizeChange(_rentalSize, event, handleChange);
                                    }}
                                    name="sizeOfRental"
                                    defaultValue={rentalSizeType}
                                    helperText={touched.sizeOfRental ? errors.sizeOfRental : ''}
                                    error={touched.sizeOfRental && !!errors.sizeOfRental}
                                    handleBlur={handleBlur}
                                />

                            </div>

                            <FormikField className='get-in-touch-form-field-right'
                                component={TextField}
                                name="company"
                                id="company"
                                type="text"
                                label="Company"
                                variant="outlined"
                                color="secondary"
                                placeholder="Company"
                                helperText={touched.company ? errors.company : ''}
                                error={touched.company && !!errors.company}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                            />
                        </div>

                        <FormikField
                            component={TextField}
                            name="typeOfBusiness"
                            id="typeOfBusiness"
                            type="text"
                            label="Type of Business"
                            variant="outlined"
                            color="secondary"
                            placeholder="Type of Business"
                            helperText={touched.typeOfBusiness ? errors.typeOfBusiness : ''}
                            error={touched.typeOfBusiness && !!errors.typeOfBusiness}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                        />

                    </>
                );
            default:
                return null;
        }
    };

    const DetailsCommon = ({ touched, errors, handleChange, handleBlur }) => {
        if (isMobile || isTablet) {
            return (
                <>
                    <div className='get-in-touch-form-dropdown-container-bottom'>

                        <Dropdown
                            placeholder="Title*"
                            selectId="title_dropdown"
                            options={titles}
                            onChange={(_title, event) => {
                                onTitleChanged(_title, event, handleChange);
                            }}
                            name="title"
                            defaultValue={titleType}
                            helperText={touched.title ? errors.title : ''}
                            error={touched.title && !!errors.title}
                            handleBlur={handleBlur}
                        />

                    </div>

                    <FormikField className='get-in-touch-form-field'
                        component={TextField}
                        required
                        name="nameAndSurname"
                        id="nameAndSurname"
                        type="text"
                        label="Name and Surname"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.nameAndSurname ? errors.nameAndSurname : ''}
                        error={touched.nameAndSurname && !!errors.nameAndSurname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                    <FormikField className='get-in-touch-form-field'
                        component={TextField}
                        required
                        name="contactNumber"
                        id="contactNumber"
                        type="tel"
                        label="Contact Number"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.contactNumber ? errors.contactNumber : ''}
                        error={touched.contactNumber && !!errors.contactNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                    <FormikField className='get-in-touch-form-field'
                        component={TextField}
                        required
                        name="emailAddress"
                        id="emailAddress"
                        type="email"
                        label="Email Address"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.emailAddress ? errors.emailAddress : ''}
                        error={touched.emailAddress && !!errors.emailAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                </>
            );
        }

        return (
            <>
                <div className='get-in-touch-form-input-container'>

                    <div className='get-in-touch-form-dropdown-container-right'>

                        <Dropdown
                            placeholder="Title*"
                            selectId="title_dropdown"
                            options={titles}
                            onChange={(_title, event) => {
                                onTitleChanged(_title, event, handleChange);
                            }}
                            name="title"
                            defaultValue={titleType}
                            helperText={touched.title ? errors.title : ''}
                            error={touched.title && !!errors.title}
                            handleBlur={handleBlur}
                        />

                    </div>

                    <FormikField className='get-in-touch-form-field-right'
                        component={TextField}
                        required
                        name="nameAndSurname"
                        id="nameAndSurname"
                        type="text"
                        label="Name and Surname"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.nameAndSurname ? errors.nameAndSurname : ''}
                        error={touched.nameAndSurname && !!errors.nameAndSurname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                </div>

                <div className='get-in-touch-form-input-container'>

                    <FormikField className='get-in-touch-form-field-left'
                        component={TextField}
                        required
                        name="contactNumber"
                        id="contactNumber"
                        type="tel"
                        label="Contact Number"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.contactNumber ? errors.contactNumber : ''}
                        error={touched.contactNumber && !!errors.contactNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                    <FormikField className='get-in-touch-form-field-right'
                        component={TextField}
                        required
                        name="emailAddress"
                        id="emailAddress"
                        type="email"
                        label="Email Address"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.emailAddress ? errors.emailAddress : ''}
                        error={touched.emailAddress && !!errors.emailAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                </div>
            </>
        );
    };

    const renderForm = () => {
        if (successfullySubmitted) {
            return null;
        }
        return (

            <div className='get-in-touch-form-container'>

                <div>

                    <Formik
                        initialValues={partnersInitialValues}
                        validationSchema={
                            enquiryType === 'Partners & Landlords'
                                ? partnersValidationSchema
                                : otherValidationSchema
                        }
                        onSubmit={onSubmit}
                        validateOnBlur
                        validateOnChange={false}
                    >
                        {({
                            submitForm,
                            isSubmitting,
                            touched,
                            errors,
                            handleChange,
                            handleBlur,
                        }) => (
                            <Form>

                                <FadeInAnimation>

                                    <p className='partners-form-title'>
                                        1. Select Your Enquiry
                                    </p>

                                </FadeInAnimation>

                                <div style={{ width: isMobile || isTablet ? '100%' : '48.8%' }}>

                                    <Dropdown
                                        placeholder="Enquiry Type*"
                                        selectId="enquiry_dropdown"
                                        options={enquiryTypes}
                                        onChange={(_enquiryType, event) => {
                                            onEnquiryChanged(_enquiryType, event, handleChange);
                                        }}
                                        name="enquiry"
                                        defaultValue={enquiryType}
                                        helperText={touched.enquiry ? errors.enquiry : ''}
                                        error={touched.enquiry && !!errors.enquiry}
                                        handleBlur={handleBlur}
                                    />

                                </div>

                                <FadeInAnimation>

                                    <p className='partners-form-title'>
                                        2. Enter Your Details
                                    </p>

                                </FadeInAnimation>

                                <DetailsCommon
                                    touched={touched}
                                    errors={errors}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                />

                                <DetailsOther
                                    touched={touched}
                                    errors={errors}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                />

                                <FormHelperText error>{contactUsSubmitError}</FormHelperText>

                                <FadeInAnimation>

                                    <div className='get-in-touch-form-button-container'>

                                        <Button
                                            id="partners-form-submit-button"
                                            style={{ marginTop: '26px' }}
                                            variant="contained"
                                            color="primary"
                                            isLoading={isSubmitting}
                                            onClick={submitForm}
                                        >
                                            Submit
                                        </Button>

                                    </div>

                                </FadeInAnimation>

                            </Form>
                        )}

                    </Formik>

                </div>

            </div>
        );
    };

    return (

        <div className='container-root container-root-maxWidthLg' id="get-in-touch-form">
            <>
                {!successfullySubmitted && (

                    <FadeInAnimation>

                        <div className='get-in-touch-form-header'>

                            <p className='dummy-header-three'>Get in Touch</p>

                            <p className='get-in-touch-form-paragraph'>
                                Contact us and be part of this revolution in rental and property
                                management.
                            </p>

                        </div>

                    </FadeInAnimation>
                )}

                {renderForm()}

            </>

            <ThankYouMessage />

        </div>
    );
};

export default PartnersForm;

PartnersForm.propTypes = {
    touched: PropTypes.string,
    errors: PropTypes.string,

    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
};
